/**
 * 他プレイヤーのアバター表示。
 *
 * player_state（位置・向き・移動中フラグ）は 10Hz 程度でしか届かないので、
 * 受信値を目標としてフレームごとに補間し、歩行アニメーションを付ける。
 * 状態は永続化されない完全な揮発データ。
 *
 * マテリアルは MeshStandard + G-buffer。点光源の影は SDF、太陽光の影は CSM。
 * 名前ラベルと作図ガイドは従来どおり非ライティング表示。
 */

import * as THREE from 'three';
import type { PlayerState, Vec3 } from '../shared/types';
import { bodyAabb, type Aabb } from '../shared/physics';
import { PLAYER_HEIGHT, PLAYER_WIDTH } from './player';
import { LAYER_OVERLAY, setObjectLayer } from './gbuffer';

/** マイクラのプレイヤーモデル（32px = 1.8 ブロック）に合わせた 1px の長さ */
const PX = PLAYER_HEIGHT / 32;

const HEAD = 8 * PX;
const BODY_W = 8 * PX;
const BODY_H = 12 * PX;
const BODY_D = 4 * PX;
const LIMB_W = 4 * PX;
const LIMB_H = 12 * PX;

/** 位置・向きの補間の速さ */
const POSITION_SMOOTHING = 14;
const ROTATION_SMOOTHING = 16;
/** 手足の振り幅（ラジアン） */
const SWING_AMPLITUDE = 0.85;

interface Avatar {
  root: THREE.Group;
  /** yaw を受け持つ胴体まわり */
  body: THREE.Group;
  head: THREE.Group;
  limbs: { group: THREE.Group; phase: number }[];
  label: THREE.Sprite;
  pendingPoints: THREE.Points;
  pendingGeometry: THREE.BufferGeometry;
  materials: THREE.Material[];
  target: PlayerState;
  /** 補間中の表示上の位置・向き */
  position: THREE.Vector3;
  yaw: number;
  pitch: number;
  /** 歩行アニメーションの位相 */
  walk: number;
}

function darken(color: THREE.Color, amount: number): THREE.Color {
  return color.clone().multiplyScalar(amount);
}

/** 顔のテクスチャ（目と口）。向きが一目で分かるように頭の前面へ貼る。 */
function createFaceTexture(color: THREE.Color): THREE.Texture {
  const size = 8;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = `#${color.getHexString()}`;
  ctx.fillRect(0, 0, size, size);
  ctx.fillStyle = '#f4f4f4';
  ctx.fillRect(1, 3, 2, 2);
  ctx.fillRect(5, 3, 2, 2);
  ctx.fillStyle = '#20242c';
  ctx.fillRect(2, 3, 1, 2);
  ctx.fillRect(5, 3, 1, 2);
  ctx.fillStyle = 'rgba(0,0,0,0.35)';
  ctx.fillRect(3, 6, 2, 1);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestFilter;
  return texture;
}

function createLabel(name: string, color: string): THREE.Sprite {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 64;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = 'rgba(16, 18, 24, 0.72)';
  ctx.beginPath();
  ctx.roundRect(0, 8, canvas.width, 48, 10);
  ctx.fill();
  ctx.fillStyle = color;
  ctx.font = 'bold 28px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(name.slice(0, 14), canvas.width / 2, 32);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
    const sprite = new THREE.Sprite(
    new THREE.SpriteMaterial({ map: texture, transparent: true, depthTest: false, toneMapped: false })
  );
  sprite.scale.set(1.6, 0.4, 1);
  sprite.position.set(0, PLAYER_HEIGHT + 0.35, 0);
  sprite.renderOrder = 3;
  return sprite;
}

/** 上端（pivot）で回転する手足を作る */
function createLimb(
  material: THREE.Material,
  width: number,
  height: number,
  depth: number,
  x: number,
  pivotY: number
): THREE.Group {
  const group = new THREE.Group();
  group.position.set(x, pivotY, 0);
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(width, height, depth), material);
  mesh.position.y = -height / 2;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  group.add(mesh);
  return group;
}

function enableShadows(root: THREE.Object3D): void {
  root.traverse((object) => {
    if (object instanceof THREE.Mesh) {
      object.castShadow = true;
      object.receiveShadow = true;
    }
  });
}

export type SetupLitMaterial = (material: THREE.Material) => void;

export class PlayerAvatars {
  private readonly group = new THREE.Group();
  private readonly avatars = new Map<string, Avatar>();
  private readonly setupLitMaterial: SetupLitMaterial;
  private localRoot: THREE.Group | null = null;
  private localBody: THREE.Group | null = null;
  private localMaterials: THREE.Material[] = [];

  constructor(scene: THREE.Scene, setupLitMaterial: SetupLitMaterial) {
    this.setupLitMaterial = setupLitMaterial;
    scene.add(this.group);
  }

  get count(): number {
    return this.avatars.size;
  }

  /** SDF に焼く他プレイヤーの体 AABB（足元基準）。 */
  bodyAabbs(): Aabb[] {
    const boxes: Aabb[] = [];
    for (const avatar of this.avatars.values()) {
      boxes.push(
        bodyAabb(
          { x: avatar.position.x, y: avatar.position.y, z: avatar.position.z },
          {
            width: PLAYER_WIDTH,
            height: avatar.target.sneaking ? PLAYER_HEIGHT - 0.22 : PLAYER_HEIGHT,
          }
        )
      );
    }
    return boxes;
  }

  /** サーバーから届いた状態を目標値として登録する */
  update(state: PlayerState): void {
    const avatar = this.avatars.get(state.playerId) ?? this.create(state);
    avatar.target = state;
  }

  /**
   * 自分自身の影を落とし受ける簡易ボディ。
   * 一人称なので頭は付けず、胴体と手足だけをカメラ下に置く。
   */
  syncLocalPlayer(position: Vec3, yaw: number, options: { sneaking: boolean; flying: boolean; moving: boolean }): void {
    if (!this.localRoot) {
      this.createLocalBody();
    }
    const root = this.localRoot!;
    const body = this.localBody!;
    root.position.set(position.x, position.y, position.z);
    body.rotation.y = yaw;
    body.position.y = options.sneaking ? -0.22 : 0;
    body.rotation.x = options.sneaking ? 0.3 : 0;
  }

  private createLocalBody(): void {
    const color = new THREE.Color('#7aa2ff');
    const shirt = new THREE.MeshStandardMaterial({
      color,
      roughness: 0.78,
      metalness: 0.05,
      colorWrite: false,
      depthWrite: false,
    });
    const pants = new THREE.MeshStandardMaterial({
      color: darken(color, 0.55),
      roughness: 0.78,
      metalness: 0.05,
      colorWrite: false,
      depthWrite: false,
    });
    this.localMaterials = [shirt, pants];

    const root = new THREE.Group();
    const body = new THREE.Group();

    const torso = new THREE.Mesh(new THREE.BoxGeometry(BODY_W, BODY_H, BODY_D), shirt);
    torso.position.y = LIMB_H + BODY_H / 2;
    body.add(torso);

    const shoulderY = LIMB_H + BODY_H;
    const armOffset = BODY_W / 2 + LIMB_W / 2;
    body.add(createLimb(shirt, LIMB_W, LIMB_H, LIMB_W, -armOffset, shoulderY));
    body.add(createLimb(shirt, LIMB_W, LIMB_H, LIMB_W, armOffset, shoulderY));
    body.add(createLimb(pants, LIMB_W, LIMB_H, LIMB_W, -LIMB_W / 2, LIMB_H));
    body.add(createLimb(pants, LIMB_W, LIMB_H, LIMB_W, LIMB_W / 2, LIMB_H));

    root.add(body);
    enableShadows(root);
    this.group.add(root);
    this.localRoot = root;
    this.localBody = body;
  }

  private makeStandard(params: THREE.MeshStandardMaterialParameters): THREE.MeshStandardMaterial {
    const material = new THREE.MeshStandardMaterial({
      roughness: 0.78,
      metalness: 0.05,
      ...params,
    });
    this.setupLitMaterial(material);
    return material;
  }

  private create(state: PlayerState): Avatar {
    const color = new THREE.Color(state.color || '#8ab4ff');
    const shirt = this.makeStandard({ color });
    const pants = this.makeStandard({ color: darken(color, 0.55) });
    const skin = this.makeStandard({ color: darken(color, 1.25) });
    const faceTexture = createFaceTexture(darken(color, 1.25));
    const face = this.makeStandard({ map: faceTexture });

    const root = new THREE.Group();
    const body = new THREE.Group();

    const torso = new THREE.Mesh(new THREE.BoxGeometry(BODY_W, BODY_H, BODY_D), shirt);
    torso.position.y = LIMB_H + BODY_H / 2;
    body.add(torso);

    const head = new THREE.Group();
    head.position.y = LIMB_H + BODY_H;
    // BoxGeometry のマテリアル順は [+X, -X, +Y, -Y, +Z, -Z]。
    // yaw=0 は -Z 方向を向くので、-Z 面に顔を貼る。
    const headMesh = new THREE.Mesh(new THREE.BoxGeometry(HEAD, HEAD, HEAD), [
      skin,
      skin,
      skin,
      skin,
      skin,
      face,
    ]);
    headMesh.position.y = HEAD / 2;
    head.add(headMesh);
    body.add(head);

    const shoulderY = LIMB_H + BODY_H;
    const armOffset = BODY_W / 2 + LIMB_W / 2;
    const limbs = [
      { group: createLimb(shirt, LIMB_W, LIMB_H, LIMB_W, -armOffset, shoulderY), phase: 1 },
      { group: createLimb(shirt, LIMB_W, LIMB_H, LIMB_W, armOffset, shoulderY), phase: -1 },
      { group: createLimb(pants, LIMB_W, LIMB_H, LIMB_W, -LIMB_W / 2, LIMB_H), phase: -1 },
      { group: createLimb(pants, LIMB_W, LIMB_H, LIMB_W, LIMB_W / 2, LIMB_H), phase: 1 },
    ];
    for (const limb of limbs) body.add(limb.group);

    const label = createLabel(state.name, state.color || '#ffffff');
    setObjectLayer(label, LAYER_OVERLAY);
    root.add(body, label);
    enableShadows(root);

    const pendingGeometry = new THREE.BufferGeometry();
    const pendingPoints = new THREE.Points(
      pendingGeometry,
      new THREE.PointsMaterial({ color, size: 0.2, depthTest: false, toneMapped: false })
    );
    pendingPoints.renderOrder = 2;
    pendingPoints.visible = false;
    pendingPoints.castShadow = false;
    setObjectLayer(pendingPoints, LAYER_OVERLAY);

    this.group.add(root, pendingPoints);

    const avatar: Avatar = {
      root,
      body,
      head,
      limbs,
      label,
      pendingPoints,
      pendingGeometry,
      materials: [shirt, pants, skin, face],
      target: state,
      position: new THREE.Vector3(state.position.x, state.position.y, state.position.z),
      yaw: state.yaw,
      pitch: state.pitch,
      walk: 0,
    };
    root.position.copy(avatar.position);
    this.avatars.set(state.playerId, avatar);
    return avatar;
  }

  /** フレームごとの補間・アニメーション更新 */
  tick(dt: number): void {
    const positionBlend = 1 - Math.exp(-POSITION_SMOOTHING * dt);
    const rotationBlend = 1 - Math.exp(-ROTATION_SMOOTHING * dt);

    for (const avatar of this.avatars.values()) {
      const target = avatar.target;
      const previous = avatar.position.clone();

      avatar.position.lerp(
        new THREE.Vector3(target.position.x, target.position.y, target.position.z),
        positionBlend
      );
      avatar.root.position.copy(avatar.position);

      avatar.yaw += shortestAngle(avatar.yaw, target.yaw) * rotationBlend;
      avatar.pitch += (target.pitch - avatar.pitch) * rotationBlend;
      avatar.body.rotation.y = avatar.yaw;
      avatar.head.rotation.x = -avatar.pitch;

      // 実際に動いた距離で位相を進めると、速度に応じた自然な歩幅になる
      const travelled = Math.hypot(
        avatar.position.x - previous.x,
        avatar.position.z - previous.z
      );
      // 補間が追いついて travelled が 0 になっても、moving の間は振り続ける
      const stride = travelled > 1e-4 ? travelled * 4 : target.moving ? dt * 8 : 0;
      avatar.walk += stride;

      const swing = stride > 0 ? Math.sin(avatar.walk) * SWING_AMPLITUDE : 0;
      const flying = target.flying;
      avatar.limbs.forEach((limb, index) => {
        // 飛行中は手足を軽く広げて、歩いていないことが分かるようにする
        if (flying) {
          limb.group.rotation.x = index < 2 ? -0.35 : 0.25;
          limb.group.rotation.z = index < 2 ? limb.phase * 0.25 : 0;
        } else {
          limb.group.rotation.x = swing * limb.phase;
          limb.group.rotation.z = 0;
        }
      });
      avatar.body.position.y = target.sneaking ? -0.22 : 0;
      avatar.body.rotation.x = target.sneaking ? 0.3 : 0;

      const pending = target.pending ?? [];
      if (pending.length > 0) {
        const positions = new Float32Array(pending.length * 3);
        pending.forEach((p, i) => {
          positions[i * 3] = p.x;
          positions[i * 3 + 1] = p.y;
          positions[i * 3 + 2] = p.z;
        });
        avatar.pendingGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        avatar.pendingGeometry.computeBoundingSphere();
        avatar.pendingPoints.visible = true;
      } else {
        avatar.pendingPoints.visible = false;
      }
    }
  }

  /** 表示中のアバターの足元位置（デバッグ表示用） */
  positionOf(playerId: string): Vec3 | null {
    const avatar = this.avatars.get(playerId);
    if (!avatar) return null;
    return { x: avatar.position.x, y: avatar.position.y, z: avatar.position.z };
  }

  remove(playerId: string): void {
    const avatar = this.avatars.get(playerId);
    if (!avatar) return;
    this.group.remove(avatar.root, avatar.pendingPoints);
    avatar.root.traverse((object) => {
      if (object instanceof THREE.Mesh) object.geometry.dispose();
    });
    for (const material of avatar.materials) {
      if (material instanceof THREE.MeshStandardMaterial) material.map?.dispose();
      material.dispose();
    }
    (avatar.label.material as THREE.SpriteMaterial).map?.dispose();
    avatar.label.material.dispose();
    avatar.pendingGeometry.dispose();
    (avatar.pendingPoints.material as THREE.Material).dispose();
    this.avatars.delete(playerId);
  }

  clear(): void {
    for (const playerId of [...this.avatars.keys()]) this.remove(playerId);
  }

  disposeLocal(): void {
    if (!this.localRoot) return;
    this.group.remove(this.localRoot);
    this.localRoot.traverse((object) => {
      if (object instanceof THREE.Mesh) object.geometry.dispose();
    });
    for (const material of this.localMaterials) material.dispose();
    this.localMaterials = [];
    this.localRoot = null;
    this.localBody = null;
  }
}

/** a から b への最短角度差（-π 〜 π） */
function shortestAngle(a: number, b: number): number {
  let diff = (b - a) % (Math.PI * 2);
  if (diff > Math.PI) diff -= Math.PI * 2;
  if (diff < -Math.PI) diff += Math.PI * 2;
  return diff;
}
